/**
 * RemoteBusContext — Item 5B (Feb 2026)
 *
 * Real-time remote-command channel. Opens a WebSocket to `/api/ws/commands`
 * on any authed venue tab that is currently the audio Master (per
 * DeviceContext.isMaster) and dispatches incoming server-side commands to
 * the MusicContext functions that already existed for the 1s-polling
 * fallback (play, pause, nextTrack, previousTrack, setVolume, etc.).
 *
 * The 1s poll in DJ Console / mini-player is left in place intentionally —
 * if the WS drops on a flaky network mid-set, the fallback still catches
 * every command within a second. Deduplication is done by `command.id`
 * (Mongo _id from `remote_commands`) — the poll's `consumed=true` mutation
 * plus this in-memory Set together guarantee each command runs exactly once.
 *
 * Reconnect: exponential backoff, capped at 15s. If the server closes with
 * code 4401 (auth invalid) we stop retrying — the user is logged out.
 */
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useDevice } from './DeviceContext';

const RemoteBusContext = createContext(null);

// LRU-ish dedupe set — capped so we don't leak IDs forever on a long
// running master session.
const MAX_DEDUP = 500;

const buildWsUrl = () => {
  const base = process.env.REACT_APP_BACKEND_URL || '';
  // http(s) → ws(s), preserve host, add path.
  return base.replace(/^http/i, 'ws') + '/api/ws/commands';
};

export const RemoteBusProvider = ({ children, dispatch }) => {
  const { user } = useAuth();
  const { isMaster, deviceId, enabled, ready } = useDevice();

  const [status, setStatus] = useState('idle'); // idle | connecting | connected | reconnecting | stopped
  const wsRef = useRef(null);
  const backoffRef = useRef(0);
  const retryTimerRef = useRef(null);
  const stoppedRef = useRef(false);
  const seenRef = useRef(new Set());
  const seenListRef = useRef([]);

  // Keep a stable ref to the dispatch fn so the WS onmessage handler
  // doesn't need to be recreated when MusicContext re-renders.
  const dispatchRef = useRef(dispatch);
  useEffect(() => { dispatchRef.current = dispatch; }, [dispatch]);

  const rememberId = useCallback((id) => {
    if (!id) return false;
    if (seenRef.current.has(id)) return true;
    seenRef.current.add(id);
    seenListRef.current.push(id);
    if (seenListRef.current.length > MAX_DEDUP) {
      const old = seenListRef.current.shift();
      seenRef.current.delete(old);
    }
    return false;
  }, []);

  const onMessage = useCallback((ev) => {
    let msg;
    try { msg = JSON.parse(ev.data); }
    catch { return; }
    if (!msg || typeof msg !== 'object') return;
    if (msg.type === 'hello') return; // just a keepalive-of-sorts
    if (msg.type !== 'command') return;
    const alreadySeen = rememberId(msg.id);
    if (alreadySeen) return;
    try {
      const fn = dispatchRef.current;
      if (typeof fn === 'function') fn(msg.command, msg.payload || {});
    } catch (err) {
      // Never let a bad command tear down the WS listener.
      // eslint-disable-next-line no-console
      console.warn('[RemoteBus] dispatch failed for', msg.command, err);
    }
  }, [rememberId]);

  const closeSocket = useCallback(() => {
    const sock = wsRef.current;
    wsRef.current = null;
    if (sock) {
      try { sock.onopen = null; sock.onmessage = null; sock.onclose = null; sock.onerror = null; sock.close(); } catch { /* noop */ }
    }
  }, []);

  const scheduleReconnect = useCallback(() => {
    if (stoppedRef.current) return;
    // Exp backoff: 1s, 2s, 4s, 8s, 15s cap.
    const delay = Math.min(15_000, 1000 * Math.pow(2, backoffRef.current));
    backoffRef.current = Math.min(4, backoffRef.current + 1);
    setStatus('reconnecting');
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    retryTimerRef.current = setTimeout(() => { openSocket(); }, delay);
  }, []); // openSocket referenced below; safe: it's a stable useCallback

  const openSocket = useCallback(() => {
    if (stoppedRef.current) return;
    closeSocket();
    setStatus('connecting');
    let url = buildWsUrl();
    if (deviceId) url += `?device_id=${encodeURIComponent(deviceId)}`;
    let sock;
    try { sock = new WebSocket(url); }
    catch (err) {
      // Malformed URL / network stack refused — fall back to reconnect loop.
      // eslint-disable-next-line no-console
      console.warn('[RemoteBus] WebSocket ctor threw:', err);
      scheduleReconnect();
      return;
    }
    wsRef.current = sock;
    sock.onopen = () => {
      backoffRef.current = 0;
      setStatus('connected');
    };
    sock.onmessage = onMessage;
    sock.onerror = () => {
      // Don't act here — the 'close' event will follow and handle reconnect.
    };
    sock.onclose = (ev) => {
      if (stoppedRef.current) { setStatus('stopped'); return; }
      // 4401 = server said "auth invalid" — stop retrying, user is logged out.
      if (ev.code === 4401) {
        stoppedRef.current = true;
        setStatus('stopped');
        return;
      }
      scheduleReconnect();
    };
  }, [closeSocket, deviceId, onMessage, scheduleReconnect]);

  // Lifecycle: open the socket ONLY when (a) authed venue user, (b) this
  // tab is the master AND (c) DeviceContext has resolved (`ready`). The
  // `ready` guard avoids opening a socket on the initial optimistic-master
  // render, which would just close it a moment later and log a benign but
  // noisy "closed before established" warning in dev consoles.
  useEffect(() => {
    const shouldConnect = !!(user && enabled && ready && isMaster);
    stoppedRef.current = !shouldConnect;
    if (shouldConnect) {
      backoffRef.current = 0;
      openSocket();
    } else {
      closeSocket();
      if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
      setStatus('idle');
    }
    return () => {
      stoppedRef.current = true;
      closeSocket();
      if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
    };
  }, [user, enabled, ready, isMaster, openSocket, closeSocket]);

  const value = {
    status,
    // Shared dedup helper — used by the home page's 1s poll consumer to
    // skip a command that was already executed by the WS listener. When
    // both delivery paths race the same command id, whichever fires first
    // adds it to the set; the loser silently drops it. `markHandled` is
    // idempotent and returns TRUE if the id was already known.
    markHandled: (id) => rememberId(id),
  };
  return <RemoteBusContext.Provider value={value}>{children}</RemoteBusContext.Provider>;
};

export const useRemoteBus = () => {
  const ctx = useContext(RemoteBusContext);
  if (!ctx) return { status: 'idle', markHandled: () => false };
  return ctx;
};
